import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect } from '@/components/forms/FormField';
import { useUpsertTier } from '@/hooks/queries/useTiers';
import { supabase } from '@/lib/supabase';
import type { TierRow, TypeTier } from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  defaultActiviteId?: string;
  defaultType: TypeTier;
  /** Forcer le sélecteur type (laisser libre si plusieurs types possibles). */
  lockType?: boolean;
  onCreated: (tier: TierRow) => void;
}

const TYPES: TypeTier[] = [
  'client_b2b',
  'client_b2c',
  'fournisseur',
  'formateur',
  'sous_traitant',
  'partenaire',
];

const TYPE_LABEL: Record<TypeTier, string> = {
  client_b2b: 'Client B2B',
  client_b2c: 'Client B2C',
  fournisseur: 'Fournisseur',
  formateur: 'Formateur',
  sous_traitant: 'Sous-traitant',
  partenaire: 'Partenaire',
};

export function TierQuickCreateDialog({
  open,
  onOpenChange,
  entrepriseId,
  defaultActiviteId,
  defaultType,
  lockType = false,
  onCreated,
}: Props): JSX.Element {
  const upsert = useUpsertTier();

  const [type, setType] = useState<TypeTier>(defaultType);
  const [raisonSociale, setRaisonSociale] = useState('');
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [iban, setIban] = useState('');
  const [contactNom, setContactNom] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setType(defaultType);
    setRaisonSociale('');
    setCode('');
    setEmail('');
    setTelephone('');
    setIban('');
    setContactNom('');
    setError(null);
  }, [open, defaultType]);

  const isFournisseur = type === 'fournisseur' || type === 'sous_traitant' || type === 'formateur';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!raisonSociale.trim()) return setError('Raison sociale obligatoire');
    try {
      const tier = await upsert.mutateAsync({
        entreprise_id: entrepriseId,
        type,
        raison_sociale: raisonSociale.trim(),
        code: code.trim() || null,
        email: email.trim() || null,
        telephone: telephone.trim() || null,
        activite_principale_id: defaultActiviteId ?? null,
      });
      // Note : iban + contact ne sont pas dans UpsertTierInput de Sprint 1, on patch
      // séparément si renseignés.
      if (iban.trim() || contactNom.trim()) {
        await supabase
          .from('tiers')
          .update({
            iban: iban.trim() || null,
            contact_nom: contactNom.trim() || null,
          })
          .eq('id', tier.id);
      }
      toast.success(`Tiers créé : ${tier.raison_sociale}`);
      onCreated(tier);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Création rapide d'un tiers"
      description="Champs minimaux. La fiche complète peut être éditée depuis Paramètres > Tiers."
      onSubmit={handleSubmit}
      isSubmitting={upsert.isPending}
      submitLabel="Créer le tiers"
      size="lg"
    >
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormSelect
          label="Type"
          value={type}
          onChange={(v) => setType(v as TypeTier)}
          required
          disabled={lockType}
          options={TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
        />
        <FormField
          label="Code interne (optionnel)"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="ex: PREST-203"
        />
        <FormField
          label="Raison sociale / Nom complet"
          value={raisonSociale}
          onChange={(e) => setRaisonSociale(e.target.value)}
          required
          fullWidth
        />
        <FormField
          label="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <FormField
          label="Téléphone"
          value={telephone}
          onChange={(e) => setTelephone(e.target.value)}
        />
        {isFournisseur && (
          <>
            <FormField
              label="IBAN / RIB (recommandé pour virements)"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="00400000000000000123"
              fullWidth
            />
            <FormField
              label="Contact"
              value={contactNom}
              onChange={(e) => setContactNom(e.target.value)}
              fullWidth
            />
          </>
        )}
      </div>
    </FormDialog>
  );
}
